import React from 'react'

/** Allowed avatar size tokens */
type AvatarSize = 's' | 'm' | 'l' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'

interface AvatarProps {
    /** Visual size of the avatar */
    size?: AvatarSize
    /** Bootstrap rounding modifier appended as `rounded-{rounded}`. Pass falsy to use bare `rounded`. */
    rounded?: string
    /** Image source URL or array of sources */
    src?: string | string[]
    /** Display name — initials are extracted automatically unless `isExact` is set */
    name?: string
    /** Extra CSS classes on the outer wrapper */
    className?: string
    /** Extra CSS classes on the media element */
    mediaClass?: string
    /** When true the full `name` string is rendered instead of extracted initials */
    isExact?: boolean
}

/**
 * Renders a user avatar with an image or name-based fallback.
 */
const Avatar = ({
    size = 'xl',
    rounded = 'circle',
    src,
    name,
    className,
    mediaClass,
    isExact = false,
}: AvatarProps) => {
    const classNames = ['avatar', `avatar-${size}`, className].join(' ')
    const mediaClasses = [
        rounded ? `rounded-${rounded}` : 'rounded',
        mediaClass,
    ].join(' ')

    const getAvatar = () => {
        if (src) {
            const imgSrc = Array.isArray(src) ? src[0] : src
            return <img className={mediaClasses} src={imgSrc} alt='' />
        }

        if (name) {
            return (
                <div className={`avatar-name ${mediaClasses}`}>
                    <span>{isExact ? name : name.match(/\b\w/g)?.join('')}</span>
                </div>
            )
        }

        return null
    }

    return <div className={classNames}>{getAvatar()}</div>
}

export default Avatar
